import React, { useCallback, useState } from "react";
import { KernelType, DatasetName, DATASETS } from "./invis-types";
import { customColors } from "../util/Theme";

interface InVisControlsProps {
  onReset: () => void;
  labelMin: number;
  labelMax: number;
  kernel: KernelType;
  dataset: DatasetName;
  onKernelChange: (kernel: KernelType) => void;
  onDatasetChange: (dataset: DatasetName) => void;
}

export const InVisControls: React.FC<InVisControlsProps> = ({
  onReset,
  labelMin,
  labelMax,
  kernel,
  dataset,
  onKernelChange,
  onDatasetChange,
}) => {
  const [showHelp, setShowHelp] = useState(() => {
    return !localStorage.getItem("invis-help-dismissed");
  });

  const dismissHelp = useCallback(() => {
    setShowHelp(false);
    localStorage.setItem("invis-help-dismissed", "1");
  }, []);

  const buttonBase: React.CSSProperties = {
    padding: "6px 14px",
    borderRadius: 5,
    fontFamily: "'Figtree', sans-serif",
    fontSize: 14,
    cursor: "pointer",
    border: `1px solid ${customColors.foreground}`,
    transition: "background 0.15s, color 0.15s",
  };

  const toggleButton = (active: boolean): React.CSSProperties => ({
    ...buttonBase,
    border: "none",
    borderRadius: 0,
    background: active ? customColors.foreground : "transparent",
    color: active ? customColors.background : customColors.foreground,
    fontWeight: active ? 600 : 400,
  });

  const toggleGroup: React.CSSProperties = {
    display: "flex",
    gap: 0,
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #ccc",
    borderRadius: 5,
    overflow: "hidden",
  };

  return (
    <>
      {/* Top controls bar */}
      <div
        style={{
          position: "absolute",
          top: 14,
          right: 14,
          display: "flex",
          gap: 10,
          alignItems: "center",
          zIndex: 5,
        }}
      >
        {/* Dataset selector */}
        <div style={toggleGroup}>
          {DATASETS.map((ds) => (
            <button
              key={ds.id}
              onClick={() => onDatasetChange(ds.id)}
              style={toggleButton(dataset === ds.id)}
            >
              {ds.label}
            </button>
          ))}
        </div>

        {/* Kernel selector - linear first */}
        <div style={toggleGroup}>
          {(["linear", "rbf"] as KernelType[]).map((k) => (
            <button
              key={k}
              onClick={() => onKernelChange(k)}
              style={toggleButton(kernel === k)}
            >
              {k === "rbf" ? "RBF" : "Linear"}
            </button>
          ))}
        </div>

        {/* Reset button */}
        <button
          onClick={onReset}
          style={{
            ...buttonBase,
            background: customColors.foreground,
            color: customColors.background,
            border: "none",
          }}
        >
          Reset
        </button>
      </div>

      {/* Color legend - bottom right */}
      <div
        style={{
          position: "absolute",
          bottom: 18,
          right: 18,
          background: "rgba(255,255,255,0.92)",
          border: "1px solid #ccc",
          borderRadius: 5,
          padding: "10px 14px",
          fontSize: 13,
          fontFamily: "'Figtree', sans-serif",
          zIndex: 5,
        }}
      >
        <div style={{ marginBottom: 4, fontWeight: 600 }}>Label</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>{labelMin.toFixed(1)}</span>
          <div
            style={{
              width: 110,
              height: 14,
              borderRadius: 3,
              background: "linear-gradient(to right, rgb(68,1,84), rgb(59,82,139), rgb(33,145,140), rgb(94,201,98), rgb(253,231,37))",
            }}
          />
          <span>{labelMax.toFixed(1)}</span>
        </div>
      </div>

      {/* Help overlay - bottom left */}
      {showHelp && (
        <div
          style={{
            position: "absolute",
            bottom: 18,
            left: 18,
            background: "rgba(255,255,255,0.95)",
            border: `1px solid ${customColors.foreground}`,
            borderRadius: 5,
            padding: "14px 18px",
            fontSize: 14,
            fontFamily: "'Figtree', sans-serif",
            maxWidth: 340,
            zIndex: 5,
            lineHeight: 1.6,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 6 }}>How to interact</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li><strong>Right-click</strong> a point to pin/unpin it as a control point</li>
            <li><strong>Drag</strong> a pinned point to reshape the embedding</li>
            <li><strong>Scroll</strong> to zoom, <strong>drag</strong> empty space to pan</li>
            <li><strong>Hover</strong> to see name &amp; label value</li>
          </ul>
          <button
            onClick={dismissHelp}
            style={{
              marginTop: 10,
              background: "none",
              border: `1px solid ${customColors.foreground}`,
              borderRadius: 4,
              padding: "4px 12px",
              fontFamily: "'Figtree', sans-serif",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            Got it
          </button>
        </div>
      )}
    </>
  );
};
